import type { EstimateLine, VatMode } from "@/types/mvpEstimate";

/** Suma netto linii (bez marży): ilość × (robocizna jedn. + materiał jedn.). */
export function lineNetSubtotal(line: EstimateLine): number {
  return line.quantity * (line.laborUnitPrice + line.materialUnitPrice);
}

/** Kwota marży w PLN. */
export function lineMarginAmount(line: EstimateLine): number {
  return lineNetSubtotal(line) * (line.marginPercent / 100);
}

/** Cena końcowa linii (netto + marża). */
export function lineFinalPrice(line: EstimateLine): number {
  return lineNetSubtotal(line) + lineMarginAmount(line);
}

export function roundMoney(n: number): number {
  return Math.round(n * 100) / 100;
}

export function totalFinalPrice(lines: EstimateLine[]): number {
  return roundMoney(lines.reduce((s, l) => s + lineFinalPrice(l), 0));
}

/** Suma robocizny w cenach końcowych (z marżą) — spójna z sumą pozycji. */
export function totalLaborFinal(lines: EstimateLine[]): number {
  return roundMoney(
    lines.reduce((s, l) => s + l.quantity * l.laborUnitPrice * (1 + l.marginPercent / 100), 0)
  );
}

/** Suma materiałów w cenach końcowych (z marżą) — spójna z sumą pozycji. */
export function totalMaterialFinal(lines: EstimateLine[]): number {
  return roundMoney(
    lines.reduce((s, l) => s + l.quantity * l.materialUnitPrice * (1 + l.marginPercent / 100), 0)
  );
}

export function vatRateFromMode(mode: VatMode): number {
  if (mode === "8") return 0.08;
  if (mode === "23") return 0.23;
  return 0;
}

/** Ułamek VAT z linii (0 przy braku / starym zapisie). */
export function effectiveLineVatRate(line: EstimateLine): number {
  const r = line.vatRate;
  return typeof r === "number" && !Number.isNaN(r) ? r : 0;
}

/** VAT od kwoty netto linii (net × stawka). */
export function lineVatAmount(line: EstimateLine): number {
  return roundMoney(lineFinalPrice(line) * effectiveLineVatRate(line));
}

/** Kwota brutto linii (net + VAT). */
export function lineGrossPrice(line: EstimateLine): number {
  return roundMoney(lineFinalPrice(line) + lineVatAmount(line));
}

/**
 * Domyślne pola trust/review dla starych rekordów i częściowych zapisów.
 * `sourceType`: z legacy `source` — generator → heuristic (heurystyka kreatora), inaczej → manual_edit.
 */
export function normalizeEstimateLineTrust(line: EstimateLine): EstimateLine {
  const sourceType: EstimateLine["sourceType"] =
    line.sourceType ??
    (line.source === "generator" ? "heuristic" : "manual_edit");
  return {
    ...line,
    sourceType,
    confidence: line.confidence ?? "medium",
    reviewStatus: line.reviewStatus ?? "draft",
    assumptionText: line.assumptionText ?? null,
    notes: line.notes ?? null,
    quantityBasis: line.quantityBasis ?? null,
  };
}

/** Uzupełnia brakujący `vatRate` po wczytaniu zapisu (nie nadpisuje istniejącej liczby). */
export function normalizeLinesVatRates(lines: EstimateLine[], mode: VatMode): EstimateLine[] {
  const fallback = vatRateFromMode(mode);
  return lines.map((l) =>
    normalizeEstimateLineTrust({
      ...l,
      vatRate: typeof l.vatRate === "number" && !Number.isNaN(l.vatRate) ? l.vatRate : fallback,
    })
  );
}

/** Narzuca jedną stawkę VAT na wszystkie linie (akcja globalna — zawsze nadpisuje `vatRate`). */
export function applyGlobalVatRateToLines(lines: EstimateLine[], mode: VatMode): EstimateLine[] {
  const rate = vatRateFromMode(mode);
  return lines.map((l) =>
    normalizeEstimateLineTrust({
      ...l,
      vatRate: rate,
    })
  );
}

export type EstimateVatSummary = {
  laborNet: number;
  materialNet: number;
  /** Suma pozycji netto (z marżą) — baza pod VAT. */
  netTotal: number;
  vatAmount: number;
  grossTotal: number;
};

/**
 * Podsumowanie oferty — **VAT = suma VAT z pozycji** (`vatRate` na każdej linii).
 * **Brutto = suma brutto pozycji** (`lineGrossPrice`), spójne z kolumną w tabeli.
 */
export function estimateVatSummary(lines: EstimateLine[]): EstimateVatSummary {
  const laborNet = totalLaborFinal(lines);
  const materialNet = totalMaterialFinal(lines);
  const netTotal = totalFinalPrice(lines);
  const vatAmount = roundMoney(lines.reduce((s, l) => s + lineVatAmount(l), 0));
  const grossTotal = roundMoney(lines.reduce((s, l) => s + lineGrossPrice(l), 0));
  return { laborNet, materialNet, netTotal, vatAmount, grossTotal };
}
